import { supabase } from './supabase';

export interface ChecklistSetupResult {
  success: boolean;
  checklistsCreated: number;
  itemsCreated: number;
  message: string;
  warning?: string;
  error?: string;
  alreadyExists?: boolean;
}

export interface ChecklistSetupOptions {
  projectId: string;
  projectType: string;
  createDefaultChecklist: boolean;
  userId: string;
  linkToSchedule?: boolean;
  force?: boolean;
}

/**
 * Set up the default checklist for a project by calling the
 * jphph-setup-checklist Edge Function (service role — bypasses RLS).
 *
 * CONFIRMED SCHEMA (2026-05-13):
 * - jphph_projects: id(uuid)
 * - jphph_checklists: project_id(TEXT NOT NULL)
 * - jphph_checklist_items: project_id(TEXT NOT NULL)
 *
 * The Edge Function uses the service role key so RLS is bypassed.
 * This is the correct pattern for server-side bulk inserts.
 */
export async function setupProjectChecklist(
  options: ChecklistSetupOptions
): Promise<ChecklistSetupResult> {
  const { projectId, createDefaultChecklist, force = false } = options;

  // CRITICAL: project_id in checklist tables is TEXT, not UUID.
  const projectIdText = String(projectId);

  // eslint-disable-next-line no-console
  console.log('New Project checklist creation debug', {
    projectId: projectIdText,
    projectIdType: typeof projectIdText,
    projectType: options.projectType,
    createDefaultChecklist,
    force,
    userId: options.userId,
    method: 'edge-function',
  });

  if (!createDefaultChecklist) {
    // eslint-disable-next-line no-console
    console.log('[ChecklistSetup] Skipped — createDefaultChecklist=false');
    return {
      success: true,
      checklistsCreated: 0,
      itemsCreated: 0,
      message: 'No default checklist requested.',
    };
  }

  if (!projectIdText || projectIdText === 'undefined' || projectIdText === 'null') {
    const err = 'Invalid projectId passed to setupProjectChecklist';
    // eslint-disable-next-line no-console
    console.error('[ChecklistSetup] FATAL:', err);
    return {
      success: false,
      checklistsCreated: 0,
      itemsCreated: 0,
      message: err,
      error: err,
    };
  }

  // Call the Edge Function which uses service role key to bypass RLS
  // eslint-disable-next-line no-console
  console.log('[ChecklistSetup] Calling jphph-setup-checklist edge function...', {
    projectId: projectIdText,
    force,
  });

  try {
    const { data, error } = await supabase.functions.invoke('jphph-setup-checklist', {
      body: {
        projectId: projectIdText,
        force,
      },
    });

    // eslint-disable-next-line no-console
    console.log('[ChecklistSetup] Edge function response:', { data, error });

    if (error) {
      const errMsg = `Edge function error: ${error.message || String(error)}`;
      // eslint-disable-next-line no-console
      console.error('[ChecklistSetup] Edge function FAILED:', errMsg);
      return {
        success: false,
        checklistsCreated: 0,
        itemsCreated: 0,
        message: errMsg,
        error: errMsg,
        warning: errMsg,
      };
    }

    if (!data) {
      const errMsg = 'Edge function returned no data';
      // eslint-disable-next-line no-console
      console.error('[ChecklistSetup] Edge function returned no data');
      return {
        success: false,
        checklistsCreated: 0,
        itemsCreated: 0,
        message: errMsg,
        error: errMsg,
      };
    }

    // eslint-disable-next-line no-console
    console.log('Default checklist creation result', {
      checklistRecordsCreated: data.checklistsCreated ?? 0,
      checklistItemsCreated: data.itemsCreated ?? 0,
      success: data.success,
      message: data.message,
      error: data.error || null,
      warning: data.warning || null,
      alreadyExists: data.alreadyExists || false,
    });

    if (!data.success) {
      const errMsg = data.error || data.message || 'Checklist creation failed';
      return {
        success: false,
        checklistsCreated: data.checklistsCreated ?? 0,
        itemsCreated: data.itemsCreated ?? 0,
        message: errMsg,
        error: errMsg,
        warning: errMsg,
      };
    }

    return {
      success: true,
      checklistsCreated: data.checklistsCreated ?? 1,
      itemsCreated: data.itemsCreated ?? 0,
      message: data.message || `Created checklist with ${data.itemsCreated ?? 0} items.`,
      warning: data.warning || undefined,
      alreadyExists: data.alreadyExists || false,
    };

  } catch (err: unknown) {
    const errMsg = err instanceof Error ? err.message : String(err);
    // eslint-disable-next-line no-console
    console.error('[ChecklistSetup] Exception calling edge function:', errMsg);
    return {
      success: false,
      checklistsCreated: 0,
      itemsCreated: 0,
      message: `Checklist creation failed: ${errMsg}`,
      error: errMsg,
      warning: `Checklist creation failed: ${errMsg}`,
    };
  }
}